/**
 * FkLookupValueButton — replaces the free-text value input in a filter row
 * when the operator is `lookup`. Shows the current FK value (and the parent
 * table's display column when available) and opens FkLookupPicker on click.
 */

import { useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { FkLookupPicker } from '../../../table-explorer/components/FkLookupPicker';
import { useFkLookupLabel } from '../../hooks/useFkLookupLabel';
import type { FilterGroup } from '../../../core/types/workspaceTypes';
import type { DatabaseEngineId } from '../../../../../core/engines/engineTypes';

type FkLookupValueButtonProps = {
  connectionId: string;
  database: string;
  /** Source column name on the current table (used in picker title). */
  sourceColumn: string;
  /** Source-side schema for loose-FK target picker context. */
  sourceSchema?: string;
  /** Source-side table for loose-FK target picker context. */
  sourceTable?: string;
  /** Source-side column data type for inference. */
  sourceColumnDataType?: string;
  /** Empty string = no declared relationship; the picker shows the target-selection UI. */
  referencedSchema: string;
  referencedTable: string;
  referencedColumn: string;
  value: string | number | bigint | boolean | Date | null | undefined;
  /**
   * Called when the user picks a row. Receives the value and the optional
   * `sourcePath` of the loose-FK declaration the picker resolved (Pass 3
   * Slice C). Hosts driving loose-FK filter conditions thread that path
   * back into `condition.sourcePath` so multi-FK columns disambiguate by
   * the user's actual pick instead of a "find-first" fallback.
   */
  onChange: (
    value: string | number | boolean | Date,
    sourcePath?: string,
  ) => void;
  className?: string;
  /**
   * Optional target-filter from a loose FK's targetFilter field.
   * When present, narrows both the FK preview row and the row-picker candidates.
   */
  targetFilter?: FilterGroup[];
  /**
   * Engine of the current connection — threaded into the picker so its column-
   * filter operator dropdown can JSON-aware default the operator (Slice 6).
   * Optional because legacy real-FK callers without engine context still work
   * — the picker just falls back to the non-JSON-aware operator list.
   */
  engine?: DatabaseEngineId;
  /**
   * Active JSON `sourcePath` from the filter condition (Pass 3 Slice A).
   * When the source column hosts multiple loose FKs, this lets the picker
   * resolve the right declaration instead of falling back to "the first
   * match". `undefined` preserves the single-FK behavior.
   */
  sourcePath?: string;
  /**
   * Called when the picker's "Clear" button is invoked. Hosts wire this to
   * remove the filter entirely (committed chip → `onRemove`, draft chip →
   * `onCancel`). When omitted, the picker doesn't show its Clear button.
   * "Clear" must remove the filter from the active filter list — just
   * blanking the value would leave a `lookup = ''` chip applied to the
   * underlying query.
   */
  onClearFilter?: () => void;
  /**
   * True when `referenced*` come from a REAL FK constraint on the source
   * column (`hasRealForeignKeyTarget`). A real FK wins over any loose
   * declaration on the same column — the precedence Table Explorer and the
   * VQE column adapter apply — so the preview and the picker then read the
   * constraint's target and the caller's `targetFilter`, never a conflicting
   * declaration resolved from MetaInfo.
   */
  referencesRealForeignKey?: boolean;
};

export function FkLookupValueButton({
  connectionId,
  database,
  sourceColumn,
  sourceSchema,
  sourceTable,
  sourceColumnDataType,
  referencedSchema,
  referencedTable,
  referencedColumn,
  value,
  onChange,
  className = '',
  targetFilter,
  engine,
  sourcePath,
  onClearFilter,
  referencesRealForeignKey = false,
}: FkLookupValueButtonProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  // "42 — Acme Corp": the parent row's display column, through the same
  // declaration the picker resolves (see `useFkLookupLabel`).
  const labelText = useFkLookupLabel({
    connectionId,
    database,
    sourceColumn,
    sourceSchema,
    sourceTable,
    referencedSchema,
    referencedTable,
    referencedColumn,
    referencesRealForeignKey,
    targetFilter,
    sourcePath,
    value,
  });

  const hasValue = value !== null && value !== undefined && value !== '';
  const valueText = hasValue ? String(value) : '';

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setPickerOpen(true)}
        onMouseDown={(e) => e.preventDefault()}
        className={`text-xs bg-background px-2 py-0.5 rounded border border-input min-w-[80px] text-left hover:border-primary hover:bg-accent cursor-pointer transition-colors inline-flex items-center gap-1 ${className}`}
        title={hasValue ? `Currently: ${valueText}${labelText ? ` — ${labelText}` : ''}` : 'Pick a row from the parent table'}
        data-testid="fk-lookup-value-button"
      >
        {hasValue ? (
          <>
            <span className="font-mono">{valueText}</span>
            {labelText && (
              <span className="text-muted-foreground truncate max-w-[160px]">
                — {labelText}
              </span>
            )}
          </>
        ) : (
          <span className="text-muted-foreground italic">Pick a row…</span>
        )}
        <ChevronDown className="h-3 w-3 ml-auto opacity-60" />
      </button>
      {pickerOpen && (
        <FkLookupPicker
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          connectionId={connectionId}
          database={database}
          referencedSchema={referencedSchema}
          referencedTable={referencedTable}
          referencedColumn={referencedColumn}
          sourceLabel={sourceColumn}
          source={
            sourceSchema !== undefined && sourceTable !== undefined
              ? {
                  schema: sourceSchema,
                  table: sourceTable,
                  column: sourceColumn,
                  dataType: sourceColumnDataType ?? 'unknown',
                }
              : undefined
          }
          targetFilter={targetFilter}
          engine={engine}
          sourcePath={sourcePath}
          referencesRealForeignKey={referencesRealForeignKey}
          onSelect={onChange}
          // Show "Clear" when the host wires `onClearFilter` AND the chip
          // currently holds a value. Clear removes the filter entirely so
          // the underlying query refreshes without it — blanking the value
          // alone would leave `lookup = ''` applied.
          onClear={hasValue && onClearFilter ? onClearFilter : undefined}
          returnFocusRef={buttonRef}
        />
      )}
    </>
  );
}
